import { HttpError } from 'ajaxian';
import { Error } from '../../../../../../../ErrorHandling';
import { MissingTemplates } from '../../../../../../LearningCollections/LearningCollection/Types';
import { MissingLinkError } from '../../../../../../../LinkyLinky';
import {
  AppyError,
  MissingApiCompatibility,
  MissingApplicationId,
} from '../../../../../../../Appy';
import { FormTemplate, HasFormTemplates } from '../../../../../../../Resource/FormTemplates/Types';
import { Resource, ValidationError } from '../../../../../../../Resource/Types';

export type UserGroupMembershipsError =
  | HttpError
  | MissingTemplates
  | MissingLinkError
  | MissingApplicationId
  | MissingApiCompatibility
  | AppyError
  | Error
  | ValidationError;

export interface UserGroupMembershipsNewFormPayload {
  kind: 'new';
}

export interface ReadyArgs {
  resource: UserGroupMembershipsNewFormResource;
  template: FormTemplate;
}

export type UserGroupMembershipsNewFormResource = Resource<UserGroupMembershipsNewFormPayload> &
  HasFormTemplates;

interface Waiting {
  kind: 'waiting';
}

interface LoadingForNew {
  kind: 'loading-for-new';
}

export interface Ready {
  kind: 'ready';
  userGroupMembershipsFormResource: UserGroupMembershipsNewFormResource;
  template: FormTemplate;
}

export interface Creating {
  kind: 'creating';
  userGroupMembershipsFormResource: UserGroupMembershipsNewFormResource;
  template: FormTemplate;
  selectedUserGroupId: string;
}

export interface Succeeded {
  kind: 'succeeded';
  bulkGroupMembershipsResource: BulkGroupMembershipsResource;
}

interface LoadError {
  kind: 'load-error';
  error: UserGroupMembershipsError;
}

interface NewFormSubmissionError {
  kind: 'new-form-submission-error';
  userGroupMembershipsFormResource: UserGroupMembershipsNewFormResource;
  template: FormTemplate;
  error: UserGroupMembershipsError;
}

export const waiting = (): Waiting => ({
  kind: 'waiting',
});

export const loadingForNew = (): LoadingForNew => ({
  kind: 'loading-for-new',
});

export const ready = (readyArgs: ReadyArgs): Ready => {
  return {
    kind: 'ready',
    userGroupMembershipsFormResource: readyArgs.resource,
    template: readyArgs.template,
  };
};

export const creating = (
  userGroupMembershipsFormResource: UserGroupMembershipsNewFormResource,
  state: Ready | NewFormSubmissionError,
  selectedUserGroupId: string,
): Creating => {
  const { kind, ...previous } = state;

  return {
    kind: 'creating',
    ...previous,
    userGroupMembershipsFormResource: userGroupMembershipsFormResource,
    selectedUserGroupId,
  };
};

export const succeeded = (
  bulkGroupMembershipsResource: BulkGroupMembershipsResource,
): Succeeded => {
  return {
    kind: 'succeeded',
    bulkGroupMembershipsResource,
  };
};

export const loadError = (error: UserGroupMembershipsError): LoadError => ({
  kind: 'load-error',
  error,
});

export const newFormSubmissionError = (
  resource: UserGroupMembershipsNewFormResource,
  error: UserGroupMembershipsError,
  template: FormTemplate,
): NewFormSubmissionError => {
  return {
    kind: 'new-form-submission-error',
    userGroupMembershipsFormResource: resource,
    template: template,
    error,
  };
};

export type State =
  | Waiting
  | LoadingForNew
  | Ready
  | Creating
  | Succeeded
  | LoadError
  | NewFormSubmissionError
  | Error;

export interface BulkGroupMemberships {
  attemptedCount: number;
  successCount: number;
  groupName: string;
}

export type BulkGroupMembershipsResource = Resource<BulkGroupMemberships>;
